import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import './SafetyForumPage.css';

/** API stores English category strings; labels are translated in the UI. */
const SAFETY_FORUM_CATEGORIES = [
  { value: 'Passenger comfort & anxiety', i18nKey: 'safetyForum.catComfortAnxiety' },
  { value: 'Turbulence & passenger comfort', i18nKey: 'safetyForum.catTurbulence' },
  { value: 'Preflight passenger briefing', i18nKey: 'safetyForum.catPreflightBriefing' },
  { value: 'Weather hazards', i18nKey: 'safetyForum.catWeather' },
  { value: 'Passenger briefing (general)', i18nKey: 'safetyForum.catPaxBriefingGeneral' },
  { value: 'TEM / SRM', i18nKey: 'safetyForum.catTemSrm' },
  { value: 'Pre-flight checklist', i18nKey: 'safetyForum.catPreflightChecklist' }
];

const DEFAULT_CATEGORY = SAFETY_FORUM_CATEGORIES[0].value;

function formatBulletinParagraphs(text) {
  if (!text || !String(text).trim()) return [];
  return String(text)
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function formatBulletinDate(iso, lng) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const base = (lng || 'en').split('-')[0];
  const loc = base === 'hu' ? 'hu-HU' : base === 'he' ? 'he-IL' : 'en-GB';
  return d.toLocaleDateString(loc, {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

const SafetyForumPage = () => {
  const { t, i18n } = useTranslation();
  const { instructor } = useAuth();
  const pilotDisplayName = (instructor?.name || instructor?.username || '').trim();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    author_name: '',
    author_email: '',
    role: 'pilot',
    category: DEFAULT_CATEGORY,
    title: '',
    content: ''
  });

  const categoryLabel = useCallback(
    (stored) => {
      const row = SAFETY_FORUM_CATEGORIES.find((c) => c.value === stored);
      return row ? t(row.i18nKey) : stored;
    },
    [t]
  );

  const bulletinSerial = useCallback(
    (id) => {
      const n = Number(id);
      if (!Number.isFinite(n)) return t('safetyForum.serialUnknown');
      return `OSF-${String(n).padStart(4, '0')}`;
    },
    [t]
  );

  const emptyForm = useMemo(
    () => ({
      author_name: pilotDisplayName,
      author_email: '',
      role: 'pilot',
      category: DEFAULT_CATEGORY,
      title: '',
      content: ''
    }),
    [pilotDisplayName]
  );

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      author_name: pilotDisplayName
    }));
  }, [pilotDisplayName]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/safety-forum/posts');
      setPosts(data.posts || []);
    } catch (err) {
      setError(err.response?.data?.error || t('safetyForum.loadErr'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.post('/api/safety-forum/posts', form);
      setSuccess(t('safetyForum.submitSuccess'));
      setForm(emptyForm);
    } catch (err) {
      setError(err.response?.data?.error || t('safetyForum.submitErr'));
    }
  };

  return (
    <div className="safety-forum-page">
      <header className="sfb-masthead" aria-labelledby="sfb-main-heading">
        <div className="sfb-masthead-inner">
          <p className="sfb-masthead-kicker">{t('safetyForum.mastheadKicker')}</p>
          <h1 id="sfb-main-heading" className="sfb-masthead-title">
            {t('safetyForum.title')}
          </h1>
          <p className="sfb-masthead-sub">
            {t('safetyForum.subtitle')}
          </p>
        </div>
      </header>

      <div className="sfb-paper">
        <div className="sfb-paper-sheet">
          <div className="sfb-distrib">
            {t('safetyForum.distribLabel')}
            <span>{t('safetyForum.distribSub')}</span>
          </div>

          <div className="sfb-intro">
            {t('safetyForum.intro')}
          </div>

          {error && (
            <div className="sfb-alert sfb-alert--error" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="sfb-alert sfb-alert--success" role="status">
              {success}
            </div>
          )}

          <section className="sfb-section" aria-labelledby="sfb-submit-heading">
            <div className="sfb-section-head">
              <h2 id="sfb-submit-heading" className="sfb-section-title">
                {t('safetyForum.submitHeading')}
              </h2>
              <span className="sfb-section-note">{t('safetyForum.submitNote')}</span>
            </div>
            <form className="sfb-form" onSubmit={handleSubmit}>
              <div className="sfb-form-grid">
                <div className="sfb-field">
                  <label htmlFor="sfb-author-name">{t('safetyForum.labelAuthor')}</label>
                  <input
                    id="sfb-author-name"
                    value={form.author_name}
                    readOnly
                    autoComplete="name"
                    title={t('safetyForum.authorTitle')}
                  />
                </div>
                <div className="sfb-field">
                  <label htmlFor="sfb-author-email">{t('safetyForum.labelEmail')}</label>
                  <input
                    id="sfb-author-email"
                    type="email"
                    value={form.author_email}
                    onChange={(e) => setForm({ ...form, author_email: e.target.value })}
                    autoComplete="email"
                  />
                </div>
                <div className="sfb-field">
                  <label htmlFor="sfb-role">{t('safetyForum.labelRole')}</label>
                  <select
                    id="sfb-role"
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                  >
                    <option value="pilot">{t('safetyForum.rolePilot')}</option>
                  </select>
                </div>
                <div className="sfb-field">
                  <label htmlFor="sfb-category">{t('safetyForum.labelCategory')}</label>
                  <select
                    id="sfb-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    {SAFETY_FORUM_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{t(c.i18nKey)}</option>
                    ))}
                  </select>
                </div>
                <div className="sfb-field sfb-field--full">
                  <label htmlFor="sfb-title">{t('safetyForum.labelTitle')}</label>
                  <input
                    id="sfb-title"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    required
                  />
                </div>
                <div className="sfb-field sfb-field--full">
                  <label htmlFor="sfb-content">{t('safetyForum.labelBody')}</label>
                  <textarea
                    id="sfb-content"
                    rows={8}
                    value={form.content}
                    onChange={(e) => setForm({ ...form, content: e.target.value })}
                    required
                    placeholder={t('safetyForum.placeholderBody')}
                  />
                </div>
              </div>
              <div className="sfb-form-actions">
                <button type="submit" className="btn btn-primary">
                  {t('safetyForum.submitBtn')}
                </button>
              </div>
            </form>
          </section>

          <section className="sfb-section" aria-labelledby="sfb-list-heading">
            <div className="sfb-section-head">
              <h2 id="sfb-list-heading" className="sfb-section-title">
                {t('safetyForum.listHeading')}
              </h2>
              <span className="sfb-section-note">{t('safetyForum.listNote')}</span>
            </div>

            {loading ? (
              <p className="sfb-loading" role="status">
                {t('safetyForum.loading')}
              </p>
            ) : posts.length === 0 ? (
              <p className="sfb-empty">{t('safetyForum.empty')}</p>
            ) : (
              <ul className="sfb-bulletin-list">
                {posts.map((post) => (
                  <li key={post.id}>
                    <article className="sfb-bulletin" aria-labelledby={`sfb-post-title-${post.id}`}>
                      <div className="sfb-bulletin__banner">
                        <span className="sfb-bulletin__serial">{bulletinSerial(post.id)}</span>
                        <div className="sfb-bulletin__meta">
                          <span className="sfb-badge">{categoryLabel(post.category)}</span>
                          <span className="sfb-bulletin__dot" aria-hidden>
                            |
                          </span>
                          <span>{post.role === 'admin' ? t('safetyForum.roleAdmin') : t('safetyForum.rolePilotMeta')}</span>
                          {post.author_name ? (
                            <>
                              <span className="sfb-bulletin__dot" aria-hidden>
                                |
                              </span>
                              <span>{post.author_name}</span>
                            </>
                          ) : null}
                          <span className="sfb-bulletin__dot" aria-hidden>
                            |
                          </span>
                          <time dateTime={post.created_at}>{formatBulletinDate(post.created_at, i18n.language)}</time>
                        </div>
                      </div>
                      <div className="sfb-bulletin__body">
                        <h3 id={`sfb-post-title-${post.id}`} className="sfb-bulletin__title">
                          {post.title}
                        </h3>
                        <div className="sfb-bulletin__text">
                          {formatBulletinParagraphs(post.content).map((para, idx) => (
                            <p key={idx}>{para}</p>
                          ))}
                        </div>
                      </div>
                    </article>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default SafetyForumPage;
